/**
 * 放送地点に紐づく `contents[]` の編集 UI。
 *
 * - カテゴリのドロップダウン (自列車遅延 / 接続線遅延 / 任意テキスト) を切り替えると、
 *   そのカテゴリに必要な入力フィールドが動的に出現する。
 * - 各コンテンツは「↑」「↓」ボタンで並び替え、「× 削除」ボタンで除去できる。
 * - 「＋ コンテンツを追加」ボタンで末尾にデフォルト値のコンテンツを追加する。
 *
 * 状態管理は親コンポーネント (BroadcastPointForm) が保持し、本コンポーネントは
 * 受け取ったコンテンツ配列を表示・編集して onChange で返すだけのコントロールド
 * コンポーネントとして実装する。
 */
import type {
  BroadcastContent,
  BroadcastContentCategory,
  BroadcastSchedule,
} from '../../api/types';

import { useEffect, useRef, useState } from 'react';
import { normalizeLocalizedSsml } from '../../utils/localizedSsml';

import {
  CATEGORY_META,
  cloneContent,
  createDefaultContent,
  getCategoryColor,
  getCategoryMeta,
  isContentEnabled,
  moveContent,
  withContentEnabled,
} from '../../utils/broadcastContents';
import { issuesForContent } from '../../utils/validationIssues';
import { fillContentRouteNicknames } from '../../utils/routeNicknameDefaults';

import { BroadcastScheduleEditor } from '../BroadcastScheduleEditor';
import { HintTooltip } from '../HintTooltip';
import { TRAIN_TYPE_DEFAULT } from './constants';
import { ContentPropertyFields } from './ContentPropertyFields';
import { BroadcastLanguagesField } from './parts/BroadcastLanguagesField';
import { PlaybackRateField } from './parts/PlaybackRateField';
import { TrainNumbersField } from './parts/TrainNumbersField';
import { TrainTypeField } from './parts/TrainTypeField';
import type { BroadcastContentsEditorProps } from './types';

export function BroadcastContentsEditor({
  contents,
  onChange,
  routes = [],
  routeStops = {},
  istationsLines = [],
  gtfsStops = [],
  parentRouteId,

  parentVoiceId,
  issues = [],
  revealTarget = null,
  onUploadAudio,
  onPreviewSsml,
  headerActions,
}: BroadcastContentsEditorProps): React.JSX.Element {
  // アコーディオンで詳細表示するコンテンツの index。
  // 複数コンテンツがある場合、この 1 件のみ詳細 (スケジュール + プロパティ) を表示し、
  // 他は折りたたむ (ヘッダーのみ)。コンテンツが 1 件のときは常にその 1 件を開く。
  const [openIndex, setOpenIndex] = useState<number>(0);

  /*
   * フッター側の追加ボタン (BroadcastPointForm) からコンテンツが増えた場合は、
   * 件数の増加を契機に、追加した末尾のコンテンツを自動で開く。
   * 初回表示や編集対象の読み込みでは開き先を変えない (前回件数との比較で増加時のみ)。
   */
  const prevLengthRef = useRef(contents.length);
  useEffect(() => {
    if (contents.length > prevLengthRef.current) {
      setOpenIndex(contents.length - 1);
    }
    prevLengthRef.current = contents.length;
  }, [contents.length]);

  // コンテンツ件数が変わった (削除等) 際に openIndex が範囲外にならないよう補正する。
  useEffect(() => {
    if (contents.length === 0) return;
    if (openIndex > contents.length - 1) {
      setOpenIndex(contents.length - 1);
    }
  }, [contents.length, openIndex]);

  /*
   * エラーサマリから「該当コンテンツを開く」指示が来たらアコーディオンを展開する。
   *
   * コンテンツが 2 件以上あると折りたたまれているため、
   * 該当カードを開かないと入力欄が DOM に存在せずスクロールもフォーカスもできない。
   * `revealTarget.nonce` を依存配列に含めることで、同じコンテンツを
   * もう一度クリックした場合 (ユーザーが手で別カードを開いた後など) も再展開される。
   */
  useEffect(() => {
    if (revealTarget?.contentIndex === undefined) return;
    setOpenIndex(revealTarget.contentIndex);
    // nonce は「同じ index でも再実行させる」ためだけに依存配列へ含める。
  }, [revealTarget?.contentIndex, revealTarget?.nonce]);

  const updateAt = (index: number, content: BroadcastContent): void => {
    const next = contents.slice();
    /*
     * Issue #556: 編集のたびに言語別文面 (`*_i18n`) を正規化する。
     *
     * 以前はここで各言語のコード内既定訳を **値として補完** していたため、
     * 放送言語のチェックを入れた (= コンテンツを触った) 時点で個別文面が
     * 焼き込まれ、定型文マスタの多言語編集が反映されなくなっていた。
     *
     * 今は「空文字のキーを落とす」だけの正規化にとどめ、既定訳は placeholder
     * (薄字) で提示する。空欄のまま保存すれば放送側がマスタへフォールバックする。
     */
    next[index] = normalizeLocalizedSsml(content);
    onChange(next);
  };

  const removeAt = (index: number): void => {
    const next = contents.slice();
    next.splice(index, 1);
    onChange(next);
  };

  const moveUp = (index: number): void => {
    onChange(moveContent(contents, index, index - 1));
  };
  const moveDown = (index: number): void => {
    onChange(moveContent(contents, index, index + 1));
  };

  const handleCategoryChange = (index: number, category: BroadcastContentCategory): void => {
    // カテゴリを変えたら properties は新カテゴリのデフォルト値で初期化する
    updateAt(index, createDefaultContent(category));
  };

  /** 複数コンテンツのときだけアコーディオン (折りたたみ) を有効にする。 */
  const accordionEnabled = contents.length > 1;
  const isOpen = (index: number): boolean => !accordionEnabled || openIndex === index;

  /*
   * この放送地点の線区が持つ列車種別 (`RouteMaster.train_types`)。
   *
   * `parentRouteId` は親フォーム (BroadcastPointForm) が
   * `parseRouteDirection(values.routeDirection).routeId` で解決済みの値を渡してくる
   * (一覧の「複製」で線区を変更した場合も追従する)。ここで再度
   * routeDirection をパースする必要はない。
   *
   * マスタに `train_types` が無い線区 (= 種別を持たない線区) は空配列になり、
   * `TrainTypeField` は「全列車」のみを選択肢として表示する。
   */
  const availableTrainTypes = routes.find((r) => r.route_id === parentRouteId)?.train_types ?? [];

  return (
    <div className="contents-editor">
      <div className="contents-editor-header">
        <strong>コンテンツ ({contents.length} 件)</strong>
        {headerActions}
      </div>

      {contents.length === 0 ? (
        <div className="empty-state" style={{ padding: '12px 8px' }}>
          コンテンツは設定されていません。上下の「＋ 自列車遅延」「＋ 接続線遅延」「＋
          任意テキスト」から作成してください。
        </div>
      ) : null}

      <ol className="contents-list">
        {contents.map((content, index) => {
          const open = isOpen(index);
          /*
           * このコンテンツに紐づく入力エラー。
           *
           * 折りたたみ中でも「#2 にエラーがある」と一目で分かるように、
           * カード見出しへ ⚠ バッジ (件数付き) を出す。
           * さらに下位 (`ContentPropertyFields` → 接続線 / 方面) へも渡して、
           * タブの ⚠ マークと該当入力欄の赤枠に使う。
           */
          const contentIssues = issuesForContent(issues, index);
          // Issue #810: コンテンツ単位の有効 / 無効 (未設定 = 有効)。
          const contentEnabled = isContentEnabled(content);
          return (
            <li
              key={index}
              className={`contents-item${accordionEnabled && !open ? ' contents-item--collapsed' : ''}${
                contentIssues.length > 0 ? ' contents-item--invalid' : ''
              }${contentEnabled ? '' : ' contents-item--disabled'}`}
              data-testid={`content-row-${index}`}
              data-enabled={contentEnabled ? 'true' : 'false'}
              style={{ background: getCategoryColor(content.category) }}
            >
              <div className="contents-item-header">
                {accordionEnabled ? (
                  <button
                    type="button"
                    className="contents-item-toggle"
                    data-testid={`content-toggle-${index}`}
                    aria-expanded={open}
                    aria-label={`コンテンツ#${index + 1} を${open ? '折りたたむ' : '展開する'}`}
                    onClick={() => setOpenIndex(open ? -1 : index)}
                  >
                    {open ? '▼' : '▶'}
                  </button>
                ) : null}
                <span className="contents-item-index">#{index + 1}</span>
                {/* 折りたたみ時にも種別ラベルが見えるようにする */}
                <span className="contents-item-category-label">
                  {getCategoryMeta(content.category)?.label}
                  {/* カテゴリの説明文は常時表示せず、ⓘ のホバーで見せる。 */}
                  <HintTooltip
                    label={`コンテンツ#${index + 1} の説明`}
                    testId={`content-description-hint-${index}`}
                  >
                    {getCategoryMeta(content.category)?.description}
                  </HintTooltip>
                </span>
                {/*
                  入力エラーのバッジ。折りたたみ中でも「このカードが原因」と分かるように
                  見出し行に件数付きで出す (クリックで展開してエラー箇所へ移動できる)。
                */}
                {contentIssues.length > 0 ? (
                  <button
                    type="button"
                    className="contents-item-error-badge"
                    data-testid={`content-error-badge-${index}`}
                    aria-label={`コンテンツ#${index + 1} の入力エラー ${contentIssues.length} 件`}
                    title={contentIssues.map((i) => i.message).join('\n')}
                    onClick={() => setOpenIndex(index)}
                  >
                    ⚠ {contentIssues.length}
                  </button>
                ) : null}

                {/*
                 * カテゴリ <select>。E2E / AI エージェントが画面トップの一覧フィルタ
                 * (id="content-filter") と取り違えやすいので、明示的な data-testid を
                 * 付与してモーダル内のコンテンツカテゴリ <select> を一意に取得できるようにする。
                 * testid は 0 始まりの index を用いる (画面表示の "#i" は 1 始まりだが、
                 * 他の content-row-{index} と同じ流儀にする)。
                 */}
                <select
                  aria-label={`コンテンツ#${index + 1} カテゴリ`}
                  data-testid={`content-category-select-${index}`}
                  value={content.category}
                  onChange={(e) =>
                    handleCategoryChange(index, e.target.value as BroadcastContentCategory)
                  }
                >
                  {/*
                   * カテゴリ選択肢:
                   * - 新規作成用のドロップダウンからは旧「接続線遅延」(`connecting_line_delay`) を
                   *   非表示にする (仕様変更: 乗換駅ベースの新カテゴリ
                   *   `connecting_line_delay_by_station` に一本化)。ただしバックエンド側の
                   *   処理・データモデル・既存レコードの表示編集は残っているため、
                   *   `CATEGORY_META` からは削除せず UI レベルでのみ絞り込む。
                   * - 既存レコード編集時に現在値が絞り込みで消えると <select> が空表示に
                   *   なってしまうので、現在の `content.category` が絞り込み対象と一致する
                   *   場合はその option を明示的に残す (下記 filter 条件の第二項)。
                   */}
                  {CATEGORY_META.filter(
                    (m) =>
                      m.category !== 'connecting_line_delay' || m.category === content.category,
                  ).map((m) => (
                    <option key={m.category} value={m.category}>
                      {m.label}
                    </option>
                  ))}
                </select>

                {/*
                 * Issue #810: コンテンツ単位の有効 / 無効トグル。
                 * 一覧の放送地点トグル (Issue #679) と同じ見た目で、無効のコンテンツは
                 * 放送対象から外れる。地点との連動は親フォーム (BroadcastPointForm) が行う。
                 */}
                <label className="enabled-toggle contents-item-enabled">
                  <input
                    type="checkbox"
                    role="switch"
                    data-testid={`content-enabled-toggle-${index}`}
                    aria-label={`コンテンツ#${index + 1} の放送を${contentEnabled ? '無効' : '有効'}にする`}
                    checked={contentEnabled}
                    onChange={(e) => {
                      const next = contents.slice();
                      next[index] = withContentEnabled(content, e.target.checked);
                      onChange(next);
                    }}
                  />
                  <span className="enabled-toggle__slider" aria-hidden="true"></span>
                  <span className="enabled-toggle__text">{contentEnabled ? '有効' : '無効'}</span>
                </label>

                {/*
                 * 常時放送バッジはカード見出しからは削除した (Editor ボタン側で
                 * `schedule-summary-${index}` として同等以上のサマリを表示するため冗長)。
                 * 詳細は BroadcastScheduleEditor.tsx のコメント参照。
                 */}
                <div className="contents-item-actions">
                  <button
                    type="button"
                    onClick={() => moveUp(index)}
                    disabled={index === 0}
                    aria-label={`コンテンツ#${index + 1} を上へ`}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => moveDown(index)}
                    disabled={index === contents.length - 1}
                    aria-label={`コンテンツ#${index + 1} を下へ`}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="danger"
                    onClick={() => removeAt(index)}
                    aria-label={`コンテンツ#${index + 1} を削除`}
                  >
                    × 削除
                  </button>
                </div>
              </div>
              {/*
               * アコーディオン折りたたみ時 (accordionEnabled かつ !open) は、
               * カテゴリ説明・スケジュール・プロパティ入力を非表示にしてヘッダーのみ表示する。
               * ただしフォーム値 (values.contents) 自体は保持されるため、保存時の
               * バリデーションは折りたたまれたコンテンツも対象になる。
               */}

              {open ? (
                <>
                  {/*
                   * 放送言語 (コンテンツ単位の複数選択)。
                   *
                   * Issue #545: 同時に再生できる言語は 8 言語 (`MAX_SPOT_LANGUAGES`) まで。
                   * 上限判定と「上限である旨」の表示は `BroadcastLanguagesField` に閉じている
                   * (バックエンドも同数で 400 を返すため、画面側で 9 件目を選べないようにする)。
                   */}
                  <BroadcastLanguagesField
                    contentIndex={index}
                    category={content.category}
                    languages={content.languages}
                    onChange={(languages) =>
                      // 放送言語を追加したら、接続線の線区愛称の空欄をマスタの名称で埋める
                      // (追加した言語の愛称が空のまま保存されるのを防ぐ)。
                      updateAt(
                        index,
                        fillContentRouteNicknames({ ...cloneContent(content), languages }, routes),
                      )
                    }
                  />
                  {/*
                   * 列車種別 (train_type) 設定 (任意)。
                   *
                   * 「全列車 / 快速 / 各駅停車」をコンテンツ単位で選択する。
                   * 快速 / 各駅停車は列車種別を持つ線区 (`RouteMaster.train_types` が
                   * 定義された線区) のみ選択肢に現れる。「全列車」(既定) はキーごと
                   * 保存しない (サーバ側 `_coerce_train_type` も 'all' を None に落とす)。
                   *
                   * 値変更時は train_type プロパティのみ差し替えて updateAt で反映する。
                   */}
                  <TrainTypeField
                    contentIndex={index}
                    value={content.train_type}
                    availableTrainTypes={availableTrainTypes}
                    onChange={(nextType) => {
                      const nextContent: BroadcastContent = { ...cloneContent(content) };
                      if (nextType === undefined || nextType === TRAIN_TYPE_DEFAULT) {
                        delete nextContent.train_type;
                      } else {
                        nextContent.train_type = nextType;
                      }
                      updateAt(index, nextContent);
                    }}
                  />
                  {/*
                   * 放送対象の列車番号 (train_numbers) 設定 (任意 / Issue #530)。
                   *
                   * 列番を列挙すると、`trip_id` の列番セグメントがそのいずれかに
                   * 一致する列車でのみ放送される (OR 条件)。空欄 (= 未指定) は
                   * 全列車で、キーごと保存しない (サーバ側 `_coerce_train_numbers`
                   * も空配列を None に落とす)。`train_type` と併用した場合は
                   * AND 条件になる (詳細は `docs/train-number.md`)。
                   *
                   * 値変更時は train_numbers プロパティのみ差し替えて updateAt で反映する。
                   */}
                  <TrainNumbersField
                    contentIndex={index}
                    value={content.train_numbers}
                    onChange={(nextNumbers) => {
                      const nextContent: BroadcastContent = { ...cloneContent(content) };
                      if (nextNumbers.length === 0) {
                        delete nextContent.train_numbers;
                      } else {
                        nextContent.train_numbers = nextNumbers;
                      }
                      updateAt(index, nextContent);
                    }}
                  />
                  <ContentPropertyFields
                    content={content}
                    // Issue #439: 音声プレビュー UI の data-testid を
                    // `preview-button-<category>-<index>` 規則で一意にするために渡す。
                    contentIndex={index}
                    routes={routes}
                    routeStops={routeStops}
                    istationsLines={istationsLines}
                    // 任意テキストの放送条件「停車番線」の番線候補 (GTFS stops 全件)。
                    gtfsStops={gtfsStops}
                    parentRouteId={parentRouteId}
                    parentVoiceId={parentVoiceId}
                    // 該当入力欄の赤枠 / 接続線・方面タブの ⚠ マーク用。
                    // このコンテンツ分だけに絞って渡す (下位で contentIndex を意識せずに済む)。
                    issues={contentIssues}
                    // 接続線タブ・方面タブを自動で開くための指示 (そのまま中継する)。
                    revealTarget={revealTarget}
                    onUploadAudio={onUploadAudio}
                    onPreviewSsml={onPreviewSsml}
                    onChange={(c) => updateAt(index, c)}
                  />
                  {/*
                   * スケジュール設定 (曜日 / 時間帯 / 有効期間)。
                   *
                   * 「何を放送するか」(プロパティ) を先に入力し、「いつ・どう流すか」は
                   * 最下部でまとめて設定する並びにしている (全カテゴリ共通)。
                   * 折りたたみ状態でも現在の設定サマリ (曜日 / 時間帯 / 有効期間,
                   * もしくは「常時放送」) がボタン内のバッジに表示される。
                   *
                   * 詳細は `BroadcastScheduleEditor` を参照。値変更時は schedule プロパティ
                   * のみ差し替えて `updateAt` で反映する。
                   */}
                  <BroadcastScheduleEditor
                    value={content.schedule}
                    contentIndex={index}
                    onChange={(nextSchedule: BroadcastSchedule | undefined) => {
                      const nextContent: BroadcastContent = { ...cloneContent(content) };
                      if (nextSchedule === undefined) {
                        delete nextContent.schedule;
                      } else {
                        nextContent.schedule = nextSchedule;
                      }
                      updateAt(index, nextContent);
                    }}
                  />
                  {/*
                   * 再生速度 (playback_rate) 設定 (任意)。
                   *
                   * 端末側の HTMLAudioElement.playbackRate / ExoPlayer.setPlaybackSpeed に
                   * 反映される。1.0 (等倍) はキーごと保存しない (端末側デフォルト)。
                   * プリセット 0.75/0.85/1.0/1.15/1.25 + 自由入力 (0.5〜2.0) の 2 方式を提供。
                   *
                   * 値変更時は playback_rate プロパティのみ差し替えて updateAt で反映する。
                   */}
                  <PlaybackRateField
                    contentIndex={index}
                    value={content.playback_rate}
                    onChange={(nextRate: number | undefined) => {
                      const nextContent: BroadcastContent = { ...cloneContent(content) };
                      if (nextRate === undefined || nextRate === 1) {
                        delete nextContent.playback_rate;
                      } else {
                        nextContent.playback_rate = nextRate;
                      }
                      updateAt(index, nextContent);
                    }}
                  />
                </>
              ) : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
